import VError from 'verror';

/**
 * Walk a VError cause chain to the underlying `createHttp` reject shape (`code`, `body`, `bodyText`).
 *
 * @param {unknown} err
 * @returns {unknown}
 */
export function resolveHttpError(err) {
    let current = err;

    // Follow VError wrappers until we hit the HTTP reject from createHttp.
    while (current) {
        // Outer notify() wrapper may lack code/body; inner error has them.
        if (hasHttpErrorShape(current)) {
            return current;
        }

        // graphql-request network failures are plain objects — VError.cause throws otherwise.
        if (!(current instanceof Error)) {
            break;
        }

        const cause = VError.cause(current);
        // No further cause, or cause chain ended.
        if (!cause || cause === current) {
            break;
        }

        current = cause;
    }

    // Nothing in the chain had HTTP fields — use what we were given.
    return err;
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
function hasHttpErrorShape(err) {
    // Not an object we can read fields from.
    if (!err || typeof err !== 'object') {
        return false;
    }

    // createHttp always sets xhr.status on reject.
    if (typeof err.code === 'number') {
        return true;
    }

    // Urban JSON body, including empty {} on some responses.
    if (err.body !== undefined && err.body !== null) {
        return true;
    }

    // Non-JSON error responses still carry text on bodyText.
    if (typeof err.bodyText === 'string' && err.bodyText.trim() !== '') {
        return true;
    }

    return false;
}

const GENERIC_4XX_TOAST_TITLE = "We couldn't complete that request";
/** Toast title for HTTP 5xx — distinct from unknown so title/body do not both say "Something went wrong". */
const SERVER_ERROR_TOAST_TITLE = 'Server error';
/** Toast title when there is no numeric HTTP status (GraphQL wrappers, plain Errors, etc.). */
const UNKNOWN_ERROR_TOAST_TITLE = 'Request could not be completed';

/** @type {Readonly<Record<number, string>>} */
const TOAST_TITLE_BY_STATUS_CODE = {
    400: 'Please check your input',
    401: 'Sign-in required',
    403: "You don't have permission to do this",
    404: 'Not found',
    405: "This action isn't allowed",
    409: 'This conflicts with another change',
    413: 'File is too large',
    429: 'Too many requests — try again later',
};

/**
 * Short user-facing toast title from HTTP status (4xx vs 5xx), not dev error text.
 *
 * @param {unknown} err
 * @returns {string}
 */
export function getDefaultToastTitle(err) {
    const resolved = resolveHttpError(err);
    const code = resolved?.code;

    // GraphQL / network / non-HTTP errors have no numeric status.
    if (typeof code !== 'number') {
        return UNKNOWN_ERROR_TOAST_TITLE;
    }

    // Known status → specific user-facing label.
    if (code in TOAST_TITLE_BY_STATUS_CODE) {
        return TOAST_TITLE_BY_STATUS_CODE[code];
    }

    // Unlisted client error (e.g. 418, 422).
    if (code >= 400 && code < 500) {
        return GENERIC_4XX_TOAST_TITLE;
    }

    // All server errors (500, 502, 503, …) share one title.
    if (code >= 500) {
        return SERVER_ERROR_TOAST_TITLE;
    }

    // Non-HTTP numeric codes (rare) — same as unknown.
    return UNKNOWN_ERROR_TOAST_TITLE;
}

/**
 * Shared helper to extract a user-facing message from Urban HTTP errors (`createHttp` reject shape).
 *
 * Urban may return:
 * - a JSON array of strings (e.g. `["Forbidden"]` on 403),
 * - a JSON array of `{ field, message }` validation errors,
 * - a plain string body (e.g. `"Password expired"`),
 * - or an object with a `message` property.
 *
 * @param {{ body?: unknown, bodyText?: string }} err
 * @param {string} [fallback=''] - Used when body/bodyText contain no usable message
 * @returns {string} User-facing plain text. Multiple array items are joined with `\n`
 *   (not HTML). Toasts display each line via `.toast-message { white-space: pre-line }`
 *   in `client/styles/styles-v5/base.less`; plain-text consumers (login forms, etc.) use
 *   the same string without that CSS.
 */
export function getApiErrorMessage(err, fallback = '') {
    const resolved = resolveHttpError(err);
    const body = resolved?.body;

    // Urban UserError: string[] or { field, message }[].
    if (Array.isArray(body) && body.length) {
        const messages = body
            .map(item => (typeof item === 'string' ? item : item?.message))
            .filter(message => typeof message === 'string' && message.trim() !== '');

        // At least one non-empty message in the array.
        if (messages.length) {
            // Plain-text line breaks; toasts honor \n via #toast-container .toast-message pre-line in base.less.
            return messages.join('\n');
        }
    }

    // Single object body with a top-level message field.
    if (body !== null && body !== undefined && typeof body === 'object' && !Array.isArray(body)) {
        if (typeof body.message === 'string' && body.message.trim() !== '') {
            return body.message.trim();
        }
    }

    // Plain string body (e.g. "Password expired").
    if (typeof body === 'string' && body.trim() !== '') {
        return body.trim();
    }

    // Last resort when responseJSON was empty but responseText had content.
    if (typeof resolved?.bodyText === 'string' && resolved.bodyText.trim() !== '') {
        return resolved.bodyText.trim();
    }

    return fallback;
}
